// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import "firebase/auth";
import "firebase/firestore";

import { getStore, useSelector } from "store";

import { Store } from "redux";
import { UNSET_USER_ID } from "components/TestRun/TestRunConstants";
import firebase from "firebase/app";
import { getFirebaseApp } from "components/Firebase/Firebase";
import { initializeEpistore } from "./epistore";

// --------------------------------------------------------------------------
// Logged-in user state
// --------------------------------------------------------------------------

// There are four major auth states:
//
// { loading: true }
// We are still loading the auth info.
//
// { loading: false, error: non-null }
// There was an error during authentication.
//
// { loading: false, auth: null }
// The user is logged out.
//
// { loading: false, auth: { ... } }
// The user is logged in.
export type AuthState = {
  loading: boolean;
  auth?: null | {
    // NOTE: The fields here are based on a firebase User object.
    uid: string;
    emailVerified: boolean;
    isGuest?: boolean;
    email: null | string;
    orgId: null | string;
  };
  error?: firebase.auth.Error;
};

export type AuthAction =
  | { type: "AUTH_LOGGED_OUT" }
  | {
      type: "AUTH_LOGGED_IN";
      uid: string;
      emailVerified: boolean;
      email: string;
      orgId: null | string;
    }
  | { type: "AUTH_ERROR"; error: firebase.auth.Error };

export function authReducer(
  state = {
    loading: true,
  },
  action: AuthAction
): AuthState {
  switch (action.type) {
    case "AUTH_LOGGED_IN":
      return {
        loading: false,
        auth: {
          uid: action.uid,
          emailVerified: action.emailVerified,
          isGuest: false,
          email: action.email,
          orgId: action.orgId,
        },
      };
    case "AUTH_LOGGED_OUT":
      return {
        loading: false,
        auth: {
          uid: UNSET_USER_ID,
          emailVerified: false,
          isGuest: true,
          email: null,
          orgId: null,
        },
      };
    case "AUTH_ERROR":
      return {
        loading: false,
        error: action.error,
      };
    default:
      return state;
  }
}

const dispatchAuthSignedIn = async ({
  uid,
  emailVerified,
  email,
  store,
}: {
  uid: string;
  emailVerified: boolean;
  email: string | null;
  store: Store;
}): Promise<void> => {
  const firebaseApp = await getFirebaseApp();
  const orgId = await firebaseApp.getUserOrgId(uid, email);

  store.dispatch({
    type: "AUTH_LOGGED_IN",
    uid,
    emailVerified,
    email,
    orgId,
  });
};

export async function subscribeToAuthState(store: Store) {
  const firebaseApp = await getFirebaseApp();
  firebaseApp.auth.onAuthStateChanged(
    user => {
      if (user) {
        user.getIdToken().then(token => {
          initializeEpistore(token);
        });
        dispatchAuthSignedIn({
          uid: user.uid,
          emailVerified: user.emailVerified,
          email: user.email,
          store,
        });
      } else {
        store.dispatch({
          type: "AUTH_LOGGED_OUT",
        });
      }
    },
    error => {
      store.dispatch({
        type: "AUTH_ERROR",
        error: error,
      });
    }
  );
}

/* Auth state selector */
export function useAuthState(): AuthState {
  return useSelector(state => state.auth);
}

export async function signin(
  email: string,
  password: string
): Promise<boolean> {
  const firebaseApp = await getFirebaseApp();

  const auth = await firebaseApp.signInWithEmailAndPassword(email, password);

  return !!auth;
}

export async function signout(): Promise<void> {
  const firebaseApp = await getFirebaseApp();

  await firebaseApp.signOut();
}

/*
Create user in auth system and send verification email.
*/
export async function createUser(
  email: string,
  password: string
): Promise<string> {
  const firebaseApp = await getFirebaseApp();

  const auth = await firebaseApp.createUserWithEmailAndPassword(
    email,
    password
  );
  if (!auth || !auth.user) {
    throw new Error("User creation failed.");
  }

  await auth.user.sendEmailVerification();
  return auth.user.uid;
}

export async function resetPassword(email: string) {
  const firebaseApp = await getFirebaseApp();

  await firebaseApp.passwordReset(email!);
}

export function isSignedIn() {
  const { auth: authState } = getStore().getState();
  return authState && authState.auth?.uid !== "UNSET_USER_ID";
}
